import { defineStore } from 'pinia';
import { Route } from 'vue-router';
import { find, merge } from 'lodash-es';
import { getSysRouter as fetchRouters } from '@/api/router';
import { routes } from '@/router';
import { flatTree } from '@/utils/base';

const getInitialPath = (routes: any[]): string => {
  if (routes.length === 0) return '/';
  if (routes[0].children?.length) {
    return getInitialPath(routes[0].children);
  } else {
    return routes[0].path;
  }
};

export const useRouterStore = defineStore('routerStore', () => {
  const sysRouter = useLocalStorage<any[] | null>('sysRouter', null, {
    serializer: {
      read: (str) => (str ? JSON.parse(str) : null),
      write: (arr) => (arr ? JSON.stringify(arr) : ''),
    },
  }); // 接口获取的全量路由

  const currentMenu = ref(''); // 当前选中的TopNav菜单

  const breadCrumbs = useSessionStorage('breadCrumbs', [] as any[]); // 面包屑

  const sideMenu = computed(() => {
    return find(sysRouter.value, (item) => item.path === currentMenu.value)?.children || [];
  });

  const menuList = computed<any[]>(() => {
    const treeToArray: (tree: any) => any[] = (tree: any) => {
      const res = [];
      if (!tree) return [];
      for (const item of tree) {
        const { children, ...i } = item;
        if (children && children?.length) res.push(...treeToArray(children));
        res.push(i);
      }
      return res;
    };
    const list = treeToArray(sideMenu.value);
    return list;
  });

  const history = computed(() => {
    return window.history;
  });

  const sysRouterName = computed(() => {
    if (!sysRouter.value) return {};
    const transform = (list: any[]) =>
      list.reduce((r: any, item: Route) => ({ ...r, [item.path]: item.meta?.title }), {});
    return merge(transform(flatTree(sysRouter.value)), transform(routes));
  });

  const defaultRoute = computed(() => {
    if (!sysRouter.value) return '/';
    return getInitialPath(sysRouter.value);
  });

  const resetRouter = () => {
    sysRouter.value = null;
  };

  const setCurrentMenu = (path: string) => {
    currentMenu.value = path;
  };

  const getSysRouter = async () => {
    try {
      const { data }: any = await fetchRouters();
      sysRouter.value = data;
      return data;
    } catch (error) {
      return Promise.reject(error);
    }
  };

  const setBreadCrumbName = ref<Function>();
  const setHouseLayoutName = ref<Function>();

  /**
   * 白名单路由配置
   * 当访问这些路由时，不会触发顶部导航菜单的切换，保持当前菜单状态不变
   * 适用于跨系统共用的页面（如某些页面在资产系统和系统管理中都有）
   */
  const whiteListOnTopNavToggle = [
    '/asset/housingRelease',
    '/asset/content',
    '/asset/abnormalDataMonitoring',
    '/system/report/list',
    '/system/workflow',
    '/asset/roomSourceLog',
    '/system/iframe',
  ];

  return {
    sysRouter,
    currentMenu,
    breadCrumbs,
    sideMenu,
    history,
    sysRouterName,
    defaultRoute,
    whiteListOnTopNavToggle,
    resetRouter,
    setCurrentMenu,
    getSysRouter,
    setBreadCrumbName,
    setHouseLayoutName,
    menuList,
  };
});
